import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Spreadsheet from '../components/Spreadsheet';
import { getUserRole } from '../helpers/local_storage_helpers';
import { confirmDialog } from '../utils/confirmDialog';

const SheetView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sheetData, setSheetData] = useState([]);
  const [sheetName, setSheetName] = useState(`Sheet ${id}`);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [viewingUsers, setViewingUsers] = useState([]);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const isNavigatingAway = useRef(false);

  // Mock employees data
  const employees = [
    { id: 1, name: 'John Smith', role: 'Sales Rep' },
    { id: 2, name: 'Sarah Johnson', role: 'Marketing Manager' },
    { id: 3, name: 'Mike Davis', role: 'Sales Rep' },
    { id: 4, name: 'Lisa Wilson', role: 'Team Lead' },
    { id: 5, name: 'Tom Brown', role: 'Sales Rep' },
    { id: 6, name: 'Emma Taylor', role: 'Marketing Specialist' },
  ];

  // Sample lead data structure - only first few rows have data, rest are empty
  const initialLeadData = [
    { A: 'Name', B: 'Email', C: 'Phone', D: 'Company', E: 'Status', F: 'Source', G: 'Notes', H: 'Date', I: '', J: '', K: '', L: '', M: '', N: '', O: '', P: '', Q: '', R: '', S: '', T: '', U: '', V: '', W: '', X: '', Y: '', Z: '' },
    { A: 'John Doe', B: 'john@example.com', C: '+1234567890', D: 'ABC Corp', E: 'New', F: 'Website', G: 'Interested in product X', H: '2024-01-15', I: '', J: '', K: '', L: '', M: '', N: '', O: '', P: '', Q: '', R: '', S: '', T: '', U: '', V: '', W: '', X: '', Y: '', Z: '' },
    { A: 'Jane Smith', B: 'jane@example.com', C: '+0987654321', D: 'XYZ Ltd', E: 'Contacted', F: 'Referral', G: 'Follow up next week', H: '2024-01-16', I: '', J: '', K: '', L: '', M: '', N: '', O: '', P: '', Q: '', R: '', S: '', T: '', U: '', V: '', W: '', X: '', Y: '', Z: '' },
    { A: 'Bob Johnson', B: 'bob@example.com', C: '+1122334455', D: 'Tech Inc', E: 'Qualified', F: 'Cold Call', G: 'Ready to purchase', H: '2024-01-17', I: '', J: '', K: '', L: '', M: '', N: '', O: '', P: '', Q: '', R: '', S: '', T: '', U: '', V: '', W: '', X: '', Y: '', Z: '' },
  ];

  useEffect(() => {
    // Load sheet data (replace with API call)
    setIsLoading(true);
    setTimeout(() => {
      setSheetData(initialLeadData);
      setOriginalData(JSON.parse(JSON.stringify(initialLeadData))); // Deep copy for comparison
      // Mock assigned users and viewing users
      setAssignedUsers([
        { id: 1, name: 'John Smith', role: 'Sales Rep' },
        { id: 2, name: 'Sarah Johnson', role: 'Marketing Manager' },
      ]);
      setViewingUsers([
        { id: 1, name: 'John Smith', role: 'Sales Rep' },
      ]);
      setIsLoading(false);
    }, 500);
  }, [id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAssignDropdown && !event.target.closest('.assign-dropdown')) {
        setShowAssignDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssignDropdown]);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(sheetData) !== JSON.stringify(originalData);
    setHasUnsavedChanges(hasChanges);
  }, [sheetData, originalData]);

  // Handle browser back/forward and page refresh
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges && !isNavigatingAway.current) {
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    const handlePopState = async (event) => {
      if (hasUnsavedChanges && !isNavigatingAway.current) {
        event.preventDefault();
        const confirmed = await confirmDialog(
          'You have unsaved changes. Are you sure you want to leave?',
          'Unsaved Changes',
          {
            confirmText: 'Leave without saving',
            cancelText: 'Stay on page',
            type: 'warning'
          }
        );
        
        if (confirmed) {
          isNavigatingAway.current = true;
          window.history.back();
        } else {
          // Push current state back to prevent navigation
          window.history.pushState(null, '', window.location.href);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges]);

  const handleDataChange = (newData) => {
    setSheetData(newData);
    // Auto-save functionality
    debouncedSave(newData);
  };

  // Debounced save function
  const debouncedSave = (() => {
    let timeoutId;
    return (data) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        saveSheet(data);
      }, 1000);
    };
  })();

  const saveSheet = async (data) => {
    setIsSaving(true);
    try {
      // TODO: Implement API call to save sheet data
      console.log('Saving sheet data:', data);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      // Update original data after successful save
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving sheet:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    await saveSheet(sheetData);
  };

  const handleExport = () => {
    // Convert data to CSV
    const csvContent = sheetData.map(row =>
      Object.values(row).map(cell => `"${cell}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sheetName}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleAssignSheet = () => {
    if (selectedEmployee) {
      const employee = employees.find(emp => emp.id === parseInt(selectedEmployee));
      if (employee && !assignedUsers.find(user => user.id === employee.id)) {
        setAssignedUsers([...assignedUsers, employee]);
        setSelectedEmployee('');
        setShowAssignDropdown(false);
      }
    }
  };

  const handleRemoveUser = (userId) => {
    setAssignedUsers(assignedUsers.filter(user => user.id !== userId));
  };

  const handleBackClick = async () => {
    if (hasUnsavedChanges) {
      const confirmed = await confirmDialog(
        'You have unsaved changes. Are you sure you want to leave?',
        'Unsaved Changes',
        {
          confirmText: 'Leave without saving',
          cancelText: 'Stay on page',
          type: 'warning'
        }
      );
      
      if (confirmed) {
        isNavigatingAway.current = true;
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };


  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading sheet...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackClick}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ← Back
          </button>
          <h1 className="text-lg font-semibold">{sheetName}</h1>
          {isSaving && (
            <span className="text-sm text-gray-500">Saving...</span>
          )}
          {hasUnsavedChanges && !isSaving && (
            <span className="text-sm text-orange-500 font-medium">• Unsaved changes</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Assigned Users */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Assigned:</span>
            <div className="flex gap-1">
              {assignedUsers.map(user => (
                <span
                  key={user.id}
                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center gap-1"
                >
                  {user.name}
                  <button
                    onClick={() => handleRemoveUser(user.id)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Viewing Users */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Viewing:</span>
            <div className="flex gap-1">
              {viewingUsers.map(user => (
                <span
                  key={user.id}
                  className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                >
                  {user.name}
                </span>
              ))}
            </div>
          </div>

          {/* Assign Dropdown */}
          <div className="relative assign-dropdown">
            <button
              onClick={() => setShowAssignDropdown(!showAssignDropdown)}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
            >
              + Assign
            </button>
            {showAssignDropdown && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded shadow-lg z-50 min-w-48">
                <div className="p-2">
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm mb-2"
                  >
                    <option value="">Select Employee</option>
                    {employees
                      .filter(emp => !assignedUsers.find(user => user.id === emp.id))
                      .map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name} ({emp.role})
                        </option>
                      ))}
                  </select>
                  <div className="flex gap-1">
                    <button
                      onClick={handleAssignSheet}
                      className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                    >
                      Assign
                    </button>
                    <button
                      onClick={() => setShowAssignDropdown(false)}
                      className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              disabled={isSaving}
            >
              Save
            </button>
            <button
              onClick={handleExport}
              className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Spreadsheet */}
      <div className="flex-1 overflow-hidden">
        <Spreadsheet
          initialData={sheetData}
          onDataChange={handleDataChange}
          readOnly={getUserRole() !== 'admin' && getUserRole() !== 'staff'}
          sheetName={sheetName}
        />
      </div>

      {/* Status Bar */}
      <div className="bg-gray-100 border-t px-3 py-1 text-xs text-gray-600">
        <div className="flex justify-between">
          <span>Ready</span>
          <span>{sheetData.length} rows</span>
        </div>
      </div>
    </div>
  );
};

export default SheetView;


